/**
 * EarlyWarningsCard — inline version of the Early Warnings stock drill-down.
 *
 * Surfaces the same 4-layer scoring + Claude plain-English explanation that
 * the /early-warnings page shows in its modal, but rendered inline on any
 * page that has a ticker context (typically the Stock Analysis page).
 *
 * Props:
 *   ticker  – string, the symbol to score (re-fetches when it changes)
 *   compact – boolean, when true hides the flag-grids by default behind a toggle
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  AlertTriangle, Activity, Lightbulb, ChevronDown, ChevronRight, RefreshCw,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { API } from "../config/api";
import { useLiveQuote, LiveSourceLabel } from "../lib/liveQuote";

const scoreClass = (v, kind) => {
  if (v == null) return "text-slate-200";
  if (kind === "warn") return v >= 60 ? "text-rose-300" : v >= 40 ? "text-amber-300" : "text-slate-100";
  if (kind === "good") return v >= 60 ? "text-emerald-300" : v >= 40 ? "text-cyan-300" : "text-slate-100";
  return "text-amber-300";
};
const fmtPct   = (v, d = 2) => v == null ? "—" : `${v >= 0 ? "+" : ""}${Number(v).toFixed(d)}%`;
const fmtPrice = (v)        => v == null ? "—" : `$${Number(v).toFixed(2)}`;
const pctClass = (v)        => v == null ? "text-slate-300" : v >= 0 ? "text-emerald-300" : "text-rose-300";

function FlagChips({ flags, accent }) {
  if (!flags) return null;
  const acc = {
    rose:    "text-rose-200 bg-rose-900/40 border-rose-600",
    emerald: "text-emerald-200 bg-emerald-900/40 border-emerald-600",
    amber:   "text-amber-200 bg-amber-900/40 border-amber-600",
  }[accent];
  return (
    <div className="flex flex-wrap gap-1">
      {Object.entries(flags).map(([k, v]) => (
        <span key={k}
          className={`text-[9px] font-mono px-1.5 py-0.5 rounded border ${v ? acc : "text-slate-400 border-slate-700 bg-slate-900/30"}`}>
          {k}
        </span>
      ))}
    </div>
  );
}

export default function EarlyWarningsCard({ ticker, compact = false, portfolio = null }) {
  const { quote: liveQ } = useLiveQuote(portfolio ? null : ticker);  // SINGLE shared price / 1D source
  const [data, setData] = useState(null);
  const [explanation, setExplanation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expLoading, setExpLoading] = useState(false);
  const [expanded, setExpanded] = useState(!compact);
  const [error, setError] = useState(null);
  const [retryAttempt, setRetryAttempt] = useState(0);

  const isPortfolio = Array.isArray(portfolio) && portfolio.length > 0;
  const portfolioKey = isPortfolio
    ? JSON.stringify(portfolio.map(h => [h.ticker, h.market_value]))
    : null;
  const label = isPortfolio ? "PORTFOLIO" : ticker;
  // MKTAVG / PORTFOLIO are synthetic cap-weighted indexes (base 100 a year
  // ago) — dollar labels mislead, so show % offsets from the base instead.
  const isSynthetic = isPortfolio || ticker === "MKTAVG";
  const fmtSynth = (v) => v == null ? "—"
    : isSynthetic ? `${(v - 100) >= 0 ? "+" : ""}${(v - 100).toFixed(2)}%`
    : `$${Number(v).toFixed(2)}`;

  const load = useCallback(async (tk) => {
    if (!tk && !isPortfolio) return;
    setLoading(true); setError(null); setData(null); setExplanation(null);
    setRetryAttempt(0);

    // Auto-retry up to 3 attempts with exponential backoff so transient
    // Cloudflare 502/520 errors (usually caused by upstream Yahoo rate
    // limits on production) don't leave the card stuck on an error.
    for (let attempt = 1; attempt <= 3; attempt++) {
      setRetryAttempt(attempt);
      try {
        const r = isPortfolio
          ? await axios.post(`${API}/daily-rebalancing/portfolio-early-warnings`,
              { holdings: portfolio.map(h => ({ ticker: h.ticker, market_value: h.market_value })) },
              { timeout: 120000 })
          : await axios.get(`${API}/early-warnings/stock/${tk}`, { timeout: 30000 });
        setData(r.data);
        setError(null);
        setLoading(false);
        return;
      } catch (e) {
        const isLast = attempt === 3;
        if (isLast) {
          const status = e?.response?.status;
          const msg = status === 502 || status === 520 || status === 521 || status === 522 || status === 524
            ? `Origin server timed out (${status}). Try refreshing in a few seconds.`
            : e?.response?.data?.detail || e?.message || "Failed to load Early Warnings";
          setError(msg);
          setLoading(false);
          return;
        }
        // Backoff: 1s → 2s
        await new Promise((res) => setTimeout(res, 1000 * (2 ** (attempt - 1))));
      }
    }
  }, [isPortfolio, portfolioKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(ticker); }, [ticker, portfolioKey, load]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = useCallback(() => { load(ticker); }, [ticker, load]);

  const getExplain = useCallback(async (tk) => {
    if (!tk && !isPortfolio) return;
    setExpLoading(true);
    try {
      const r = isPortfolio
        ? await axios.post(`${API}/daily-rebalancing/portfolio-early-warnings/explain`,
            { holdings: portfolio.map(h => ({ ticker: h.ticker, market_value: h.market_value })), kind: "auto" },
            { timeout: 120000 })
        : await axios.post(`${API}/early-warnings/explain`,
            { symbol: tk, kind: "auto" }, { timeout: 30000 });
      setExplanation(r.data);
    } catch (e) {
      setExplanation({ explanation: e?.response?.data?.detail || e?.message || "Failed to generate explanation" });
    }
    setExpLoading(false);
  }, [isPortfolio, portfolioKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-load explanation once the data lands.
  useEffect(() => {
    if (data?.scores && (ticker || isPortfolio)) {
      getExplain(ticker);
    }
  }, [data?.scores, ticker, getExplain, isPortfolio]);

  if (!ticker && !isPortfolio) return null;

  return (
    <Card className="bg-slate-900/40 border-slate-700" data-testid="ew-stock-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2 text-white">
          <AlertTriangle className="w-4 h-4 text-amber-400" />
          Early Warnings
          <span className="font-mono text-cyan-300 font-bold ml-1">{label}</span>
          {isPortfolio && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-300 font-mono">
              {data?.n_holdings ?? portfolio.length} holdings · index base 100
            </span>
          )}
          {data?.parent_etf && (
            <a href={`/stock?ticker=${data.parent_etf}`} target="_blank" rel="noopener noreferrer"
               className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-100 font-mono hover:bg-slate-700 hover:text-white">
              parent: {data.parent_etf}
            </a>
          )}
          <span className="text-[10px] text-slate-300 font-normal ml-auto">
            4-layer scoring · D1-D18 / R1-R10 / BK1-BK18
          </span>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={loading}
            data-testid="ew-stock-refresh"
            title="Refresh Early Warnings"
            className="inline-flex items-center gap-1 rounded border border-slate-600 bg-slate-800 px-2 py-1 text-[10px] font-semibold text-slate-100 hover:bg-slate-700 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading && (
          <div data-testid="ew-stock-skel" className="space-y-2">
            <Skeleton className="h-32 bg-slate-800/60" />
            {retryAttempt > 1 && (
              <div className="text-[11px] text-amber-300/80 font-mono">
                retrying… attempt {retryAttempt}/3
              </div>
            )}
          </div>
        )}
        {error && (
          <div className="rounded border border-rose-600 bg-rose-900/30 px-3 py-2 flex flex-col sm:flex-row sm:items-center gap-2"
               data-testid="ew-stock-error">
            <div className="text-rose-200 text-sm flex-1">{error}</div>
            <button
              type="button"
              onClick={handleRefresh}
              disabled={loading}
              data-testid="ew-stock-error-retry"
              className="inline-flex items-center gap-1.5 rounded border border-rose-500 bg-rose-800/60 px-3 py-1.5 text-xs font-semibold text-rose-50 hover:bg-rose-700 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors self-start sm:self-auto"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              Retry
            </button>
          </div>
        )}

        {data?.scores && (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="ew-stock-kpis">
              <Kpi label={isSynthetic ? "Since 1Y ago" : "Last"} value={fmtSynth(isSynthetic ? data.features?.last_close : (liveQ?.price ?? data.features?.last_close))} cls="text-white"
                   sub={<LiveSourceLabel quote={liveQ} testId="ew-last-source" />} />
              <Kpi label="1D"            value={fmtPct(liveQ?.change_1d_pct ?? data.features?.r1d)}          cls={pctClass(liveQ?.change_1d_pct ?? data.features?.r1d)}
                   sub={<LiveSourceLabel quote={liveQ} testId="ew-1d-source" />} />
              <Kpi label="Deterioration" value={`${data.scores.deterioration_pct}%`} sub={data.scores.deterioration_count} cls={scoreClass(data.scores.deterioration_pct, "warn")} />
              <Kpi label="Recovery"      value={`${data.scores.recovery_pct}%`}      sub={data.scores.recovery_count}      cls={scoreClass(data.scores.recovery_pct, "good")} />
              <Kpi label="Breakout"      value={`${data.scores.breakout_pct}%`}      sub={data.scores.breakout_count}      cls="text-amber-300" />
            </div>

            {/* Plain-English explanation (auto-loaded) */}
            {expLoading && !explanation && (
              <div className="rounded border border-amber-700/70 bg-amber-900/10 p-3"
                   data-testid="ew-stock-explain-loading">
                <Skeleton className="h-4 w-32 bg-amber-900/30 mb-2" />
                <Skeleton className="h-4 w-full bg-amber-900/30 mb-1.5" />
                <Skeleton className="h-4 w-11/12 bg-amber-900/30 mb-1.5" />
                <Skeleton className="h-4 w-9/12 bg-amber-900/30" />
              </div>
            )}

            {explanation && (
              <div className="rounded border border-amber-700/70 bg-amber-900/20 p-3 text-sm text-slate-100 leading-relaxed"
                   data-testid="ew-stock-explain-panel">
                <div className="text-[10px] uppercase text-amber-200 font-bold tracking-wider mb-1.5">
                  {explanation.kind} signal {explanation.llm_used ? "· Claude" : "· templated"}
                </div>
                {explanation.explanation}
                {explanation.triggered_signals?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {explanation.triggered_signals.slice(0, 8).map(s => (
                      <span key={s} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-800 text-slate-100">
                        {s}
                      </span>
                    ))}
                  </div>
                )}
                <button
                  onClick={() => getExplain(ticker)}
                  disabled={expLoading}
                  data-testid="ew-stock-explain-btn"
                  className="mt-2 px-2 py-1 rounded border border-amber-500/60 bg-amber-600/20 text-amber-100 text-[11px] font-semibold hover:bg-amber-600/40 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                >
                  <Lightbulb className="w-3 h-3" />
                  {expLoading ? "Asking Claude…" : "Refresh explanation"}
                </button>
              </div>
            )}

            {/* Expandable flag chips */}
            <button
              onClick={() => setExpanded(v => !v)}
              data-testid="ew-stock-flags-toggle"
              className="text-[11px] text-slate-200 hover:text-white flex items-center gap-1 font-semibold"
            >
              {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              {expanded ? "Hide" : "Show"} all triggered signals
            </button>

            {expanded && (
              <div className="space-y-3" data-testid="ew-stock-flag-grids">
                <FlagSection title="D1–D18 Deterioration" flags={data.flags?.deterioration} accent="rose"    testid="ew-stock-flags-d" />
                <FlagSection title="R1–R10 Recovery"      flags={data.flags?.recovery}      accent="emerald" testid="ew-stock-flags-r" />
                <FlagSection title="BK1–BK18 Breakout"    flags={data.flags?.breakout}      accent="amber"   testid="ew-stock-flags-bk" />
                <FlagSection title="S1–S10 Stock Drop"    flags={data.flags?.stock_drop}    accent="rose"    testid="ew-stock-flags-s" />
              </div>
            )}

            {/* Stop hint */}
            <div className="text-[11px] text-slate-200 pt-1 border-t border-slate-800">
              Suggested 2×ATR stop:
              <span className="ml-1 font-mono text-amber-300 font-bold">
                {data.features?.atr10 && data.features?.last_close
                  ? fmtSynth(data.features.last_close - 2 * data.features.atr10)
                  : "—"}
              </span>
              <span className="ml-2 text-slate-100">ATR10 {data.features?.atr10?.toFixed(2) ?? "—"}</span>
              {data.features?.rs_20d_vs_spy != null && (
                <span className="ml-3 text-slate-100">RS vs SPY 20d:
                  <span className={`ml-1 font-mono font-bold ${pctClass(data.features.rs_20d_vs_spy)}`}>
                    {fmtPct(data.features.rs_20d_vs_spy)}
                  </span>
                </span>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Kpi({ label, value, sub, cls }) {
  return (
    <div className="min-w-[80px]">
      <div className="text-[10px] uppercase text-slate-200 font-semibold tracking-wider">{label}</div>
      <div className={`text-lg font-mono font-bold ${cls}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-300 font-mono">{sub}</div>}
    </div>
  );
}

function FlagSection({ title, flags, accent, testid }) {
  if (!flags) return null;
  const entries = Object.entries(flags);
  const triggered = entries.filter(([, v]) => v).length;
  return (
    <div data-testid={testid}>
      <div className="text-[12px] font-bold text-white mb-1.5">
        {title}
        <span className="text-[11px] text-slate-200 font-normal ml-1.5">{triggered}/{entries.length} triggered</span>
      </div>
      <FlagChips flags={flags} accent={accent} />
    </div>
  );
}
